import * as React from "react";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  GraphData,
  GraphDataEdge,
  GraphDataNode,
  NodeType,
  RuleType,
} from "fe-common/graphql";
import { GraphAreaSelection } from "../components/copiedGraphDetails";

export const emptyGraphSelection = {
  node: null,
  edge: null,
  detailOpen: false,
} as GraphAreaSelection;

export interface UseGraphDetailSelectionProps {
  graph: GraphData;
}

export interface GraphDetailSelection {
  className?: string;
  disease?: GraphDataNode;
  resetSelect: any;
  details: GraphAreaSelection;
  selection: GraphAreaSelection;
  setSelection: Dispatch<SetStateAction<GraphAreaSelection>>;
  hasEdgeSelect: boolean;
  findingNode?: GraphDataNode[];
  directEdge?: (node: GraphDataNode) => GraphDataEdge;
  nodes?: (e: GraphDataEdge) => [GraphDataNode, GraphDataNode];
  edgesAroundSelection: GraphDataEdge[];
}

export const useGraphDetailSelection = ({
  graph,
}: UseGraphDetailSelectionProps): GraphDetailSelection => {
  const [selection, setSelection] = useState<GraphAreaSelection>(
    emptyGraphSelection,
  );

  // clear selection if graph changes
  // TODO: add dependency
  /* eslint-disable */
  useEffect(() => {
    if (selection?.edge || selection?.node) setSelection(emptyGraphSelection);
  }, [graph]);
  const resetSelect = useCallback(() => {
    setSelection(emptyGraphSelection);
  }, [setSelection]);
  /* eslint-enable */

  // if no edge or node is selected - show all the edges
  const details = React.useMemo(
    () =>
      selection?.edge?.length || selection?.node
        ? selection
        : graph
        ? {
            edge: graph.edges
              ?.filter(
                (e) => e.type !== RuleType.Isa && e.type !== RuleType.Subtype,
              )
              .map((e) => ({
                source: graph.nodes.find((n) => n.id === e.sourceID),
                target: graph.nodes.find((n) => n.id === e.targetID),
                path: e,
              })),
          }
        : { edge: null, node: null },
    [selection, graph],
  );

  const hasEdgeSelect = selection?.edge?.length > 0;

  const disease = React.useMemo(
    () =>
      graph?.nodes
        ? graph.nodes.find((n) => n.type === NodeType.Disease)
        : null,
    [graph],
  );

  // compute direct path to node
  // TODO: add dependency
  /* eslint-disable */
  const directEdge = React.useMemo(
    () => (node: GraphDataNode) => {
      if (node) {
        return graph.edges.find(
          (e) => e.sourceID === disease?.id && e.targetID === node.id,
        );
      }
      return null;
    },
    [graph],
  );
  /* eslint-enable */
  const edgesAroundSelection = React.useMemo(() => {
    if (selection.node) {
      return [...edgesAroundNode(selection.node, graph)];
    }
  }, [graph, selection.node]);

  const findingNode = React.useMemo(
    () =>
      graph?.nodes
        ? graph.nodes.filter((n) => n.type === NodeType.Finding)
        : [],
    [graph],
  );

  const nodes = React.useMemo(() => {
    const { nodes } = graphTraverse(graph);
    return (e: GraphDataEdge) =>
      [nodes([e.sourceID])[0], nodes([e.targetID])[0]] as [
        GraphDataNode,
        GraphDataNode,
      ];
  }, [graph]);

  // const {nodes} = graphTraverse(graph);
  // if (edgesAroundSelection && edgesAroundSelection.length)
  //    console.log(edgesAroundSelection.map(e=>nodes([e.sourceID, e.targetID]).map(n=>n.qfs[0].name)));

  const ret = {
    resetSelect,
    details,
    selection,
    setSelection,
    hasEdgeSelect,
    findingNode,
    disease,
    directEdge,
    edgesAroundSelection,
    nodes,
  };
  // keep object stable so it can be used in a useMemo (es-lint complains about ret, but its ok)
  // eslint-disable-next-line
  return useMemo(() => ret, [...Object.values(ret)]);
};

export function graphTraverse(graph: GraphData) {
  const sources = (id) => graph.edges.filter((e) => e.sourceID === id);
  const targets = (id) => graph.edges.filter((e) => e.targetID === id);
  const nodes = (ids) => graph.nodes.filter((n) => ids.includes(n.id));
  return { sources, targets, nodes };
}

function edgesAroundNode(
  node: GraphDataNode,
  graph: GraphData,
): Set<GraphDataEdge> {
  const { sources, targets } = graphTraverse(graph);

  const destinationNodes = new Set(
    graph.nodes
      .filter((n) => n.type === NodeType.Disease || n.type === NodeType.Finding)
      .map((n) => n.id),
  );

  // forward search
  const forward = edgesToDestination(
    sources(node.id),
    0,
    (e) => destinationNodes.has(e.targetID),
    (e) => sources(e.targetID),
  );
  const backward = edgesToDestination(
    targets(node.id),
    0,
    (e) => destinationNodes.has(e.sourceID),
    (e) => targets(e.sourceID),
  );
  return new Set([...forward, ...backward]);
}

export const edgesToDestination = function<T>(
  start: T[],
  depth: number,
  isDestination: (f: T) => boolean,
  getEdges: (f: T) => T[],
): any {
  let complete = new Set();
  let rejected = new Set();

  let node = (t: T[], depth: number) => ({
    path: t,
    target: getEdges(t[t.length - 1]) || [],
    depth,
  });

  let endOfPath = (n) => n?.path[n?.path?.length - 1];

  // push first node
  let inprogress = start.map((s) => node([s], 0));

  // loop until q is empty
  while (inprogress.length > 0) {
    let curr = inprogress.pop();

    if (isDestination(endOfPath(curr))) {
      curr.path.forEach((p) => complete.add(p));
    } else if (curr.target.length && (depth === 0 || curr.depth < depth)) {
      let traverseInProgress = false;
      for (let target of curr.target) {
        // avoid loops
        if (!complete.has(target) && !rejected.has(target)) {
          inprogress.push(node([...curr.path, target], curr.depth + 1));
          traverseInProgress = true;
        }
      }
      if (!traverseInProgress) rejected.add(endOfPath(curr));
    }
  }
  return [...complete];
};
